/**
 * The aspects floater: which aspects survive, in what order, laid out in fixed columns.
 *
 * <p>Pure functions over the backend's aspect list. The floater used to be an HTML table, which
 * aligns by construction; these are text lines, so the alignment is arithmetic and belongs
 * somewhere it can be tested rather than inside a render.
 */

/**
 * The orb a pair is allowed, which depends on whether the Moon is in it.
 *
 * <p>Seven degrees with the Moon and five without. The Moon moves about thirteen degrees a day, so
 * five degrees of orb is under ten hours either side of exact — a window narrow enough that a daily
 * sample lands inside it mostly by luck. Every other body earns its aspect by standing there.
 */
export const ORB_LIMIT_WITH_MOON = 7.0;
export const ORB_LIMIT_DEFAULT = 5.0;

/** The three hard aspects that qualify a companion to a trine. */
export const HARD_ASPECTS = ['Conjunction', 'Square', 'Opposition'];

/** How wide a body's name column is. Neptune, Jupiter and Mercury are the longest, at seven. */
export const NAME_WIDTH = 7;

/** Exactly five characters of the aspect's name, per the study. */
export const ASPECT_WIDTH = 5;

/**
 * Whether the orb column pads with a zero or a space.
 *
 * <p>Either aligns the decimal point, which is the requirement. A zero is used because in a
 * monospace column a leading space and a dropped character look identical, and a reader comparing
 * two rows should not have to decide which they are looking at.
 */
export const ORB_PAD = '0';

/** The colours the floater has always used, kept so the reformatting does not lose that channel. */
export const ASPECT_COLORS = {
  Conjunction: '#1565c0',
  'Semi-sextile': '#7b1fa2',
  Sextile: '#00838f',
  Square: '#d84315',
  Trine: '#2e7d32',
  Quincunx: '#6a1b9a',
  Opposition: '#c62828',
};

/** The orb this pair is allowed. */
export function orbLimitFor(aspect) {
  if (!aspect) return ORB_LIMIT_DEFAULT;
  const moon = aspect.planet1 === 'Moon' || aspect.planet2 === 'Moon';
  return moon ? ORB_LIMIT_WITH_MOON : ORB_LIMIT_DEFAULT;
}

/** Aspects inside their own limit. */
export function withinOrb(aspects) {
  return (Array.isArray(aspects) ? aspects : []).filter(
    a => a && typeof a.orb === 'number' && Number.isFinite(a.orb) && a.orb <= orbLimitFor(a));
}

/**
 * The first five characters of an aspect's name, padded if it is shorter.
 *
 * <p>Truncated rather than mapped through a table of abbreviations. A table would need an entry per
 * aspect and would render an unmapped one at its full width, which is the one case that breaks the
 * column — so the rule that guarantees the width is the rule that is applied.
 */
export function abbreviateAspect(name) {
  return String(name || '').slice(0, ASPECT_WIDTH).padEnd(ASPECT_WIDTH);
}

/** `01.24°` — two integer digits, two decimals, so the points line up down the column. */
export function formatOrb(orb) {
  const n = typeof orb === 'number' && Number.isFinite(orb) ? Math.abs(orb) : 0;
  const fixed = n.toFixed(2);
  // padStart on the WHOLE figure, so a three-digit orb widens the column rather than being cut
  // down to two digits and reading as a tighter aspect than it is.
  return `${fixed.padStart(5, ORB_PAD)}°`;
}

/** `Mercury - Venus   - Trine - 01.24°` */
export function formatAspectLine(aspect) {
  if (!aspect) return '';
  const p1 = String(aspect.planet1 || '').padEnd(NAME_WIDTH);
  const p2 = String(aspect.planet2 || '').padEnd(NAME_WIDTH);
  return `${p1} - ${p2} - ${abbreviateAspect(aspect.aspect)} - ${formatOrb(aspect.orb)}`;
}

const byOrb = (a, b) => {
  if (a.orb !== b.orb) return a.orb - b.orb;
  // A stable last resort, so the same sky always lists in the same order. Without it two aspects at
  // an identical orb can swap places between two views of one bar.
  return formatAspectLine(a).localeCompare(formatAspectLine(b));
};

/**
 * The three groups, in priority order.
 *
 * <p>Group 3 is <b>everything remaining</b>, not only aspects between bodies untouched by a trine.
 * Read literally, "aspects between bodies not involved in the Group 1 trines" would silently drop a
 * sextile that happens to touch a trine planet — it is not hard, so group 2 will not have it, and it
 * is not unrelated, so group 3 would not either. Aspects that the orb filter admitted should not
 * then vanish because of where they sit in the hierarchy.
 */
export function groupAspects(aspects) {
  const kept = withinOrb(aspects);

  const trines = kept.filter(a => a.aspect === 'Trine').sort(byOrb);

  const inTrine = new Set();
  trines.forEach((a) => { inTrine.add(a.planet1); inTrine.add(a.planet2); });

  const claimed = new Set(trines);
  const companions = kept
    .filter(a => !claimed.has(a)
      && HARD_ASPECTS.includes(a.aspect)
      && (inTrine.has(a.planet1) || inTrine.has(a.planet2)))
    .sort(byOrb);
  companions.forEach(a => claimed.add(a));

  const others = kept.filter(a => !claimed.has(a)).sort(byOrb);

  return { trines, companions, others };
}

/**
 * The floater's lines, with a blank line between adjacent groups that both have content.
 *
 * <p>A separator between every pair of populated groups rather than only after the trines. The study
 * names that one explicitly, but three groups run together below one divider are two groups a reader
 * cannot tell apart — and a blank line before an empty group would be a divider dividing nothing.
 *
 * <p>Each line carries the colour of its aspect, so reformatting into text has not cost the channel
 * the table used to carry.
 */
export function buildAspectFloaterLines(aspects) {
  const { trines, companions, others } = groupAspects(aspects);

  const blocks = [trines, companions, others].filter(b => b.length > 0);
  const lines = [];
  blocks.forEach((block, i) => {
    if (i > 0) lines.push({ text: '', color: null, blank: true });
    block.forEach(a => lines.push({
      text: formatAspectLine(a),
      color: ASPECT_COLORS[a.aspect] || '#555',
      aspect: a,
      blank: false,
    }));
  });
  return lines;
}

/** What was dropped, so a short list reads as filtered rather than as a quiet sky. */
export function filterSummary(aspects) {
  const all = Array.isArray(aspects) ? aspects.length : 0;
  const kept = withinOrb(aspects).length;
  return { all, kept, dropped: all - kept };
}
